import { EMPTY_PAYMENT_METHOD, InvoiceType, OrderFields } from "../../../constants";
import type { TOrderFormData } from "../../../models/orderForm";
import getAddressString from "../../../utils/getAddressString";
import type { TPrefillInfoParams } from "./types";

const getPrefillSummaryData = ({
  storedData,
  userAddress,
  invoiceAddresses,
}: TPrefillInfoParams): Partial<TOrderFormData> => {
  const hasStoredPrivateInvoiceData =
    !!storedData?.[OrderFields.INVOICE_NAME] || !!storedData?.[OrderFields.INVOICE_EMAIL];
  const hasStoredCompanyInvoiceData =
    !!storedData?.[OrderFields.INVOICE_COMPANY_NAME] ||
    !!storedData?.[OrderFields.INVOICE_COMPANY_EMAIL] ||
    !!storedData?.[OrderFields.INVOICE_REGISTRY_CODE] ||
    !!storedData?.[OrderFields.INVOICE_VAT_NUMBER] ||
    !!storedData?.[OrderFields.INVOICE_REGISTRATION_ADDRESS];

  const prefillPrivateInvoice =
    invoiceAddresses?.private && !hasStoredPrivateInvoiceData ? invoiceAddresses.private : null;
  const prefillCompanyInvoice =
    invoiceAddresses?.company && !hasStoredCompanyInvoiceData ? invoiceAddresses.company : null;
  const prefillUserAddress = !prefillPrivateInvoice && !hasStoredPrivateInvoiceData ? userAddress : null;

  const prefillPrivateInvoiceType = prefillPrivateInvoice?.isDefault ? InvoiceType.PRIVATE : null;
  const prefillCompanyInvoiceType = prefillCompanyInvoice?.isDefault ? InvoiceType.COMPANY : null;
  const prefillInvoiceType = prefillPrivateInvoiceType || prefillCompanyInvoiceType;

  const companyInvoiceAddress = getAddressString({
    street: prefillCompanyInvoice?.street || undefined,
    houseNo: prefillCompanyInvoice?.streetNumber || undefined,
    flatNo: prefillCompanyInvoice?.flatNo || undefined,
    zip: prefillCompanyInvoice?.zip || undefined,
    country: prefillCompanyInvoice?.country || undefined,
    city: prefillCompanyInvoice?.city || undefined,
  });

  // Not prefilling default user address if there is stored different country
  const storedStartingCountry = storedData?.[OrderFields.STARTING_COUNTRY];
  const filteredUserAddress =
    !storedStartingCountry || userAddress?.countryCode === storedStartingCountry ? userAddress : null;

  const senderName = storedData?.[OrderFields.SENDER_NAME] ?? filteredUserAddress?.contactName;
  const senderEmail = storedData?.[OrderFields.SENDER_EMAIL] ?? filteredUserAddress?.contactEmail;

  const invoiceType =
    (storedData?.[OrderFields.INVOICE_TYPE] ??
      (!hasStoredPrivateInvoiceData && !hasStoredCompanyInvoiceData && prefillInvoiceType)) ||
    InvoiceType.PRIVATE;
  const invoiceName =
    storedData?.[OrderFields.INVOICE_NAME] ||
    prefillPrivateInvoice?.contactName ||
    prefillUserAddress?.contactName ||
    senderName;
  const invoiceEmail =
    storedData?.[OrderFields.INVOICE_EMAIL] ||
    prefillPrivateInvoice?.contactEmail ||
    prefillUserAddress?.contactEmail ||
    senderEmail;
  const invoiceCompanyName = storedData?.[OrderFields.INVOICE_COMPANY_NAME] ?? prefillCompanyInvoice?.companyName;
  const invoiceCompanyEmail = storedData?.[OrderFields.INVOICE_COMPANY_EMAIL] ?? prefillCompanyInvoice?.email;
  const invoiceRegistryCode = storedData?.[OrderFields.INVOICE_REGISTRY_CODE] ?? prefillCompanyInvoice?.companyCode;
  const invoiceVatNumber = storedData?.[OrderFields.INVOICE_VAT_NUMBER] ?? prefillCompanyInvoice?.companyVat;
  const invoiceRegistrationAddress =
    storedData?.[OrderFields.INVOICE_REGISTRATION_ADDRESS] ?? companyInvoiceAddress ?? undefined;

  const hasTermsAgreement = storedData?.[OrderFields.TERMS_AGREEMENT];

  const paymentMethod =
    storedData?.[OrderFields.PAYMENT_METHOD] !== EMPTY_PAYMENT_METHOD
      ? storedData?.[OrderFields.PAYMENT_METHOD]
      : undefined;

  const valuesToPrefill: Partial<TOrderFormData> = {
    [OrderFields.INVOICE_TYPE]: invoiceType,
    [OrderFields.INVOICE_NAME]: invoiceName,
    [OrderFields.INVOICE_EMAIL]: invoiceEmail,
    [OrderFields.INVOICE_COMPANY_NAME]: invoiceCompanyName,
    [OrderFields.INVOICE_COMPANY_EMAIL]: invoiceCompanyEmail,
    [OrderFields.INVOICE_REGISTRY_CODE]: invoiceRegistryCode,
    [OrderFields.INVOICE_VAT_NUMBER]: invoiceVatNumber,
    [OrderFields.INVOICE_REGISTRATION_ADDRESS]: invoiceRegistrationAddress,
    [OrderFields.TERMS_AGREEMENT]: hasTermsAgreement,
    [OrderFields.PAYMENT_METHOD]: paymentMethod,
  };
  return valuesToPrefill;
};
export default getPrefillSummaryData;
